import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  DriverViewModel,
  DriverPreferredClient,
  DriverBannedClient,
  DriverPreferredArea,
  DriverAssignmentHistory,
} from "@app/shared/models/drivers/DriverViewModel.model";
import { DispatchService } from "@app/dispatch-schedule/shared/dispatch.service";
import { ToastrService } from "ngx-toastr";
import { AssignDriverResult } from "@app/shared/components/assign-driver-modal/assign-driver-modal.component";

@Component({
  selector: "app-driver-assignments",
  templateUrl: "./driver-assignments.component.html",
  styleUrls: ["./driver-assignments.component.css"],
})
export class DriverAssignmentsComponent implements OnInit, OnChanges {
  @Input() driver: DriverViewModel;
  @Input() isEditMode: boolean;
  @Input() tractors: any[] = [];
  @Input() trailers: any[] = [];
  @Input() clients: any[] = [];
  @Input() fuelRegions: any[] = [];
  @Input() loadingTractors: boolean = false;
  @Input() loadingTrailers: boolean = false;
  @Input() loadingClients: boolean = false;
  @Input() loadingFuelRegions: boolean = false;

  // Assign to scheduling team modal
  showAssignModal = false;

  // Map of region names to countries based on fuel component regions
  private regionCountryMap: { [key: string]: string } = {
    // United States
    'US - NATIONAL': 'United States',
    'CALIFORNIA': 'United States',
    'CENTRAL ATLANTIC': 'United States',
    'GULF COAST': 'United States',
    'LOWER ATLANTIC': 'United States',
    'MIDWEST': 'United States',
    'NEW ENGLAND': 'United States',
    'ROCKY MOUNTAIN': 'United States',
    'WEST COAST LESS CALIFORNIA': 'United States',
    'EAST COAST': 'United States',
    'WEST COAST': 'United States',
    // Canada
    'CA - NATIONAL': 'Canada',
    "ST. JOHN'S": 'Canada',
    'WINNIPEG': 'Canada',
    'FREDERICTON': 'Canada',
    'LETHBRIDGE': 'Canada',
    'HALIFAX': 'Canada',
    'CHARLOTTETOWN': 'Canada',
    'MONTREAL': 'Canada',
    'REGINA': 'Canada',
    'VANCOUVER': 'Canada',
    'TORONTO': 'Canada'
  };

  @Output() hasChanges = new EventEmitter<boolean>();

  allClients: any[] = [];
  billTos: any[] = [];

  preferredClients: DriverPreferredClient[] = [];
  preferredAreas: DriverPreferredArea[] = [];
  bannedClients: DriverBannedClient[] = [];
  assignmentHistory: DriverAssignmentHistory[] = [];

  originalDriver: string;

  constructor(private dispatchService: DispatchService, private toastr: ToastrService) {}

  ngOnInit() {
    this.loadAssignmentData();
    this.originalDriver = JSON.stringify(this.getDriverState());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["clients"] && this.clients) {
      this.allClients = this.clients;
    }

    // Update bill-tos if client selection exists
    if (this.driver?.clientId) {
      this.updateBillTos();
    }
  }

  get parentClients(): any[] {
    return (this.allClients || []).filter((client) => !client.clientId);
  }

  updateBillTos() {
    if (this.driver.clientId) {
      this.billTos = (this.allClients || []).filter((bt) => bt.clientId === this.driver.clientId);
    } else {
      this.billTos = [];
    }
  }

  loadAssignmentData() {
    // Load from driver if available, otherwise initialize empty
    this.preferredClients = this.driver.preferredClients || [];
    this.preferredAreas = this.driver.preferredAreas || [];
    this.bannedClients = this.driver.bannedClients || [];
    this.assignmentHistory = this.driver.assignmentHistory || [];

    // Sort assignment history by date descending
    this.assignmentHistory.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  onClientChange() {
    this.updateBillTos();
    if (!this.driver.clientId) {
      this.driver.billToId = null;
    }
    this.checkForChanges();
  }

  addPreferredClient() {
    this.preferredClients.push({ clientId: null });
    this.checkForChanges();
  }

  removePreferredClient(index: number) {
    this.preferredClients.splice(index, 1);
    this.checkForChanges();
  }

  onPreferredClientChange() {
    this.checkForChanges();
  }

  addPreferredArea() {
    this.preferredAreas.push({ gasRegionId: null });
    this.checkForChanges();
  }

  removePreferredArea(index: number) {
    this.preferredAreas.splice(index, 1);
    this.checkForChanges();
  }

  onPreferredAreaChange() {
    this.checkForChanges();
  }

  addBannedClient() {
    this.bannedClients.push({ clientId: null });
    this.checkForChanges();
  }

  removeBannedClient(index: number) {
    this.bannedClients.splice(index, 1);
    this.checkForChanges();
  }

  onBannedClientChange() {
    this.checkForChanges();
  }

  onFieldChange() {
    this.checkForChanges();
  }

  getDriverState() {
    return {
      tractorId: this.driver.tractorId,
      trailerId: this.driver.trailerId,
      clientId: this.driver.clientId,
      billToId: this.driver.billToId,
      specialNote: this.driver.specialNote,
      preferredClients: this.preferredClients,
      preferredAreas: this.preferredAreas,
      bannedClients: this.bannedClients,
    };
  }

  checkForChanges() {
    const hasChanged = JSON.stringify(this.getDriverState()) !== this.originalDriver;
    this.hasChanges.emit(hasChanged);
  }

  trackByIndex(index: number): number {
    return index;
  }

  getClientName(clientId: string): string {
    const client = this.parentClients.find((c) => c.id === clientId);
    return client ? client.itemName : "";
  }

  getRegionName(gasRegionId: string): string {
    const fuelRegion = this.fuelRegions.find((r) => r.id === gasRegionId);
    return fuelRegion ? fuelRegion.name : gasRegionId;
  }

  getRegionsByCountry(): { country: string; regions: any[] }[] {
    const grouped: { [key: string]: any[] } = {};

    this.fuelRegions.forEach((region) => {
      const country = this.regionCountryMap[region.name] || "Other";
      if (!grouped[country]) {
        grouped[country] = [];
      }
      grouped[country].push(region);
    });

    return Object.keys(grouped)
      .sort()
      .map((country) => ({
        country,
        regions: grouped[country],
      }));
  }

  getSchedulingTeamName(): string {
    return this.driver?.schedulingTeamName || '';
  }

  getBillToName(): string {
    return this.driver?.billToClientName || '';
  }

  getBoardName(): string {
    return this.driver?.boardName || '';
  }

  // ── Assign to scheduling team ─────────────────────────────────────────────

  openAssignModal(): void {
    this.showAssignModal = true;
  }

  onAssignOrUpdateSuccess(result: AssignDriverResult): void {
    this.driver.schedulingTeamName = result.team?.name || null;
    this.driver.billToClientName = result.billToClientName;
    this.driver.boardName = result.boardName;
  }

  onUnassignSuccess(): void {
    this.driver.schedulingTeamName = null;
    this.driver.billToClientName = null;
    this.driver.boardName = null;
  }
}
